import type { ComponentType } from 'react';
import {
  AnchorId,
  CommonStepId,
  HighlightRole,
  StepActionKind,
  WorkflowId as WorkflowIdValue,
} from './Enums';
import { MaintenanceStepId, MaintenanceSubsceneId } from './MaintenanceEnums';
import { LeaseStepId } from './LeaseEnums';
import { PaymentsStepId, PaymentsSubsceneId } from './PaymentsEnums';

export enum PanelState {
  Collapsed = 'collapsed',
  Expanded = 'expanded',
}

export type WorkflowId = WorkflowIdValue;
export type TourStepId =
  | MaintenanceStepId
  | PaymentsStepId
  | LeaseStepId
  | CommonStepId;
export type TourSubsceneId = MaintenanceSubsceneId | PaymentsSubsceneId;

export type OnboardingStep =
  | 'email'
  | 'loading'
  | 'workflow-select'
  | 'quick-tips';

export type OnboardingState = {
  unlocked: boolean;
  email: string | null;
  selectedWorkflow: WorkflowId | null;
  completedWorkflows: WorkflowId[];
};

export type PaymentsLineItem = {
  id: string;
  itemType: string;
  itemName: string;
  site?: string;
  company?: string;
  description?: string;
  quantity: number;
  amount: number;
};

export type PaymentsInvoice = {
  id: string;
  docNo?: string;
  issuedDate: string;
  dueDate: string;
  balance: number;
  total: number;
  status: 'outstanding' | 'paid';
  lineItems: PaymentsLineItem[];
};

export type PaymentsTransaction = {
  id: string;
  kind: 'invoice' | 'invoicePayment';
  date: string;
  number: string;
  amount: number;
  balance: number;
  dueDate?: string;
  outstanding?: number;
  statusLabel?: string;
  lineItems?: PaymentsLineItem[];
  appliedItems?: {
    id: string;
    title: string;
    amount: number;
    quantity?: number;
    itemType?: string;
    appliedTo?: string;
    bankAccount?: string;
  }[];
};

export type MaintenanceRequestPriority = 'low' | 'medium' | 'high';

export type MaintenanceRequestStatus =
  | 'new'
  | 'open'
  | 'inProgress'
  | 'completed';

export type MaintenanceRequest = {
  id: string;
  title: string;
  category: string;
  propertyName: string;
  unitName: string;
  requestedAt: string;
  priority: MaintenanceRequestPriority;
  status: MaintenanceRequestStatus;
  vendor?: string | null;
  residentName: string;
  description: string;
  entryNotes?: string;
  contactPhone?: string;
};

export type MaintenanceData = {
  requests: MaintenanceRequest[];
};

export type TourModel = {
  mode: 'manager' | 'tenantPreview';
  tenant: {
    name: string;
    unitName: string;
    propertyName: string;
    balanceAmount: number;
    autoPayOn: boolean;
    avatarUrl?: string;
  };
  payments: {
    invoices: PaymentsInvoice[];
    transactions: PaymentsTransaction[];
  };
  maintenance: MaintenanceData;
};

export enum NavSource {
  Internal = 'internal',
  Route = 'route',
}

export type HighlightTarget = {
  anchor: AnchorId;
  role: HighlightRole;
};

export type SpotlightStyle = 'default' | 'compact';

export type SpotlightConfig = {
  anchor: AnchorId;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  highlights?: HighlightTarget[];
  style?: SpotlightStyle;
};

export type StepAction =
  | { action: StepActionKind.NEXT }
  | { action: StepActionKind.BACK }
  | { action: StepActionKind.GO_TO; to: TourStepId }
  | { action: StepActionKind.RESET }
  | { action: StepActionKind.EXIT };

export type Subscene = {
  id: TourSubsceneId;
  spotlight?: SpotlightConfig;
  coach?: {
    body: string;
    primary?: { label: string; do: StepAction };
    secondary?: { label: string; do: StepAction };
  };
};

export type Step = {
  id: TourStepId;
  title: string;
  Scene: ComponentType;
  next?: TourStepId;
  back?: TourStepId;
  subscenes?: Subscene[];
  defaultSubsceneId?: TourSubsceneId;
  coach?: {
    body: string;
    primary?: { label: string; do: StepAction };
    secondary?: { label: string; do: StepAction };
  };
  onEnter?: {
    panel?: PanelState;
  };
  spotlight?: SpotlightConfig;
};

export type TourDefinition = {
  id: WorkflowId;
  title: string;
  steps: Step[];
  initialModel: TourModel;
};
